import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import 'bootstrap/js/dist/tooltip';
import * as bootstrap from 'bootstrap';
import { toast } from 'react-toastify';

import DayDropdown from "../../component/DayDropdown";
import CustomEditor from '../../custom/CustomEditor';
import { generatePackageDescription, generatePackageOverview } from "../../custom/geminiService";
import { Autocomplete, useLoadScript } from "@react-google-maps/api";

const GOOGLE_MAP_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const libraries = ["places"];

const apiClient = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
});

apiClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// ---- helper: normalize hotel_ids to an array of numbers ----
const normalizeHotelIds = (input) => {
    if (input === undefined || input === null) return [];
    if (Array.isArray(input)) return input.flat(Infinity).map(i => Number(i)).filter(Number.isFinite);
    if (typeof input === 'string') {
        const trimmed = input.trim();
        // try JSON parse (handles "[1,2,3]" or "\"[1,2,3]\"" etc.)
        try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) return parsed.flat(Infinity).map(i => Number(i)).filter(Number.isFinite);
            if (Number.isFinite(Number(parsed))) return [Number(parsed)];
        } catch (e) {
            // not JSON, try CSV
            if (trimmed.includes(',')) {
                return trimmed.split(',').map(s => Number(s.trim())).filter(Number.isFinite);
            }
            const n = Number(trimmed);
            return Number.isFinite(n) ? [n] : [];
        }
    }
    // fallback: try to coerce iterable-like
    try {
        return Array.from(input).map(i => Number(i)).filter(Number.isFinite);
    } catch (e) {
        return [];
    }
};

// ---- Helper: normalize itinerary (keeps your original behavior) ----
const normalizeItinerary = (raw) => {
    if (!raw) return [{ title: '', description: '' }];
    if (Array.isArray(raw)) {
        return raw.map(d => ({ title: d?.title ?? '', description: d?.description ?? '' }));
    }
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) return parsed.map(d => ({ title: d?.title ?? '', description: d?.description ?? '' }));
        } catch (e) {
            // not JSON -> fallthrough to single text entry
        }
        return [{ title: '', description: String(raw) }];
    }
    if (typeof raw === 'object') {
        // If it's an object with numeric keys (like {0:{},1:{}}) -> use Object.values
        const vals = Object.values(raw);
        if (vals.length > 0 && vals.every(v => typeof v === 'object')) {
            return vals.map(d => ({ title: d?.title ?? '', description: d?.description ?? '' }));
        }
        // else wrap single object as an array
        return [{ title: raw.title ?? '', description: raw.description ?? '' }];
    }
    return [{ title: '', description: '' }];
};

function FormYatra() {
    const { id } = useParams();
    const isEditMode = Boolean(id);
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState({});

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: GOOGLE_MAP_KEY,
        libraries,
    });

    const cityAutoRef = React.useRef(null);
    
    // RENAMED: use `itinerary` to avoid collision with formData.days
    const [itinerary, setItinerary] = useState([{ title: '', description: '' }, { title: '', description: '' }]);
    const [images, setImages] = useState([]);
    const [hotels, setHotels] = useState([]);
    const [assignedHotels, setAssignedHotels] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isGeneratingAI, setIsGeneratingAI] = useState(false);
    const [isGeneratingAIDescription, setIsGeneratingAIDescription] = useState(false);
    const [isRefreshingHotels, setIsRefreshingHotels] = useState(false);
    
    // UPDATED: Added 'yatra_overview' to the main formData state.
    const [formData, setFormData] = useState({
        yatra_name: '', city: '', nights: '', days: '', description: '',
        yatra_price: '', offer_price: '', how_to_reach: '', places_to_visit: '',
        yatra_overview: '', // <-- ADDED THIS LINE
        hotel_ids: [],
    });

    useEffect(() => {
        const fetchHotelsAndYatra = async () => {
            try {
                const hotelsResponse = await apiClient.get('/hotels');
                const allHotels = hotelsResponse.data || [];
                setHotels(allHotels);

                if (isEditMode) {
                    const yatraResponse = await apiClient.get(`/yatras/${id}`);
                    const pkg = yatraResponse.data || {};
                    
                    // Normalize hotel_ids to ensure it's an array of numbers
                    const normalizedHotelIds = normalizeHotelIds(pkg.hotel_ids);

                    // UPDATED: Set yatra_overview within formData and ensure hotel_ids is array
                    setFormData({
                        yatra_name: pkg.yatra_name || '', city: pkg.city || '',
                        nights: pkg.nights || '', days: pkg.days || '',
                        description: pkg.description || '', yatra_price: pkg.yatra_price || '',
                        offer_price: pkg.offer_price || '', how_to_reach: pkg.how_to_reach || '',
                        places_to_visit: pkg.places_to_visit || '', 
                        yatra_overview: pkg.yatra_overview || '', 
                        hotel_ids: normalizedHotelIds,
                    });

                    // normalize itinerary robustly
                    setItinerary(normalizeItinerary(pkg.itinerary));

                    setImages(pkg.Images ? pkg.Images.map(img => ({ url: img.image_url, isExisting: true })) : []);
                    
                    if (normalizedHotelIds && normalizedHotelIds.length > 0 && allHotels.length > 0) {
                        const selectedHotels = allHotels.filter(h => normalizedHotelIds.includes(h.id));
                        setAssignedHotels(selectedHotels);
                    } else {
                        setAssignedHotels([]);
                    }
                }
            } catch (error) {
                console.error("Failed to fetch data:", error);
                toast.error("Could not load yatra data. Please try again.");
                navigate('/admin/yatras');
            } finally {
                setLoading(false);
            }
        };
        fetchHotelsAndYatra();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isEditMode, navigate]);

    useEffect(() => {
        if (loading) return;
        const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new bootstrap.Tooltip(tooltipTriggerEl));
        return () => {
            tooltipList.forEach(tooltip => {
                if (tooltip && typeof tooltip.dispose === 'function') {
                    tooltip.dispose();
                }
            });
        };
    }, [loading]);

    const availableHotelsForSelection = useMemo(() => {
        const assignedIds = new Set(assignedHotels.map(h => h.id));
        return hotels.filter(h => !assignedIds.has(h.id)).map(hotel => ({ value: hotel.id, label: `${hotel.hotel_name} - (${hotel.city})` }));
    }, [hotels, assignedHotels]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    };

    const handleEditorChange = (fieldName, value) => {
        setFormData((prev) => ({ ...prev, [fieldName]: value }));
        if (errors[fieldName]) setErrors(prev => ({ ...prev, [fieldName]: null }));
    };

    const onCityPlaceChanged = () => {
        const place = cityAutoRef.current?.getPlace();
        if (!place || !place.address_components) return;

        const get = (type) => place.address_components.find((c) => c.types.includes(type))?.long_name || "";
        
        let cityName = get("locality") || get("administrative_area_level_2") || place.name;
        
        setFormData((prev) => ({
            ...prev,
            city: cityName,
        }));
        if (errors.city) setErrors(prev => ({ ...prev, city: null }));
    };

    const fetchHotelsOnly = async () => {
        setIsRefreshingHotels(true);
        try {
            const hotelsRes = await apiClient.get('/hotels/all');
            setHotels(hotelsRes.data || []);
            toast.success("Hotels list refreshed successfully!");
        } catch (error) {
            console.error("Failed to refresh hotels", error);
            toast.error("Failed to refresh hotels.");
        } finally {
            setIsRefreshingHotels(false);
        }
    };
    
    const handleHotelSelect = (selectedOption) => {
        if (!selectedOption) return;
        const hotelToAdd = hotels.find(h => h.id === selectedOption.value);
        if (hotelToAdd) {
            // avoid duplicates in assignedHotels
            setAssignedHotels(prev => {
                if (prev.some(h => h.id === hotelToAdd.id)) return prev;
                return [...prev, hotelToAdd];
            });

            // safely update hotel_ids (ensure array)
            setFormData(prev => {
                const prevIds = normalizeHotelIds(prev.hotel_ids);
                if (prevIds.includes(hotelToAdd.id)) return { ...prev, hotel_ids: prevIds };
                return { ...prev, hotel_ids: [...prevIds, hotelToAdd.id] };
            });

            if (errors.hotels) setErrors(prev => ({ ...prev, hotels: null }));
        }
    };

    const handleRemoveAssignedHotel = (hotelIdToRemove) => {
        setAssignedHotels(prev => prev.filter(h => h.id !== hotelIdToRemove));
        setFormData(prev => {
            const prevIds = normalizeHotelIds(prev.hotel_ids);
            const filtered = prevIds.filter(id => id !== hotelIdToRemove);
            return { ...prev, hotel_ids: filtered };
        });
    };

    // Keep your original handler names but wire to `itinerary`
    const handleDayChange = (index, field, value) => {
        setItinerary(prev => {
            const arr = Array.isArray(prev) ? [...prev] : normalizeItinerary(prev);
            arr[index] = { ...arr[index], [field]: value };
            return arr;
        });
    };

    const handleAddDay = () => {
        setItinerary(prev => {
            const arr = Array.isArray(prev) ? [...prev] : normalizeItinerary(prev);
            return [...arr, { title: '', description: '' }];
        });
    };

    const handleRemoveDay = (indexToRemove) => {
        setItinerary(prev => {
            const arr = Array.isArray(prev) ? [...prev] : normalizeItinerary(prev);
            if (arr.length > 1) {
                return arr.filter((_, index) => index !== indexToRemove);
            }
            return arr;
        });
    };

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);
        if (images.length + files.length > 6) { toast.warn("You can upload a maximum of 6 images."); return; }
        const newImages = files.map(file => ({ file, url: URL.createObjectURL(file) }));
        setImages(prev => [...prev, ...newImages]);
        if (errors.images) setErrors(prev => ({ ...prev, images: null }));
    };

    const handleRemoveImage = (indexToRemove) => {
        const imageToRemove = images[indexToRemove];
        if (imageToRemove?.file) {
            URL.revokeObjectURL(imageToRemove.url);
        }
        setImages(prev => prev.filter((_, index) => index !== indexToRemove));
    };
    
    const isEditorEmpty = (htmlString) => !htmlString || htmlString.replace(/<[^>]*>?/gm, '').trim().length === 0;

    const handleGenerateOverview = async () => {
        const { yatra_name, city, nights, days, yatra_price } = formData;
    
        if (!yatra_name || !city || !nights || !days || !yatra_price) {
            toast.warn("Please provide Yatra Name, City, Nights, Days, and Price before generating.");
            return;
        }
    
        setIsGeneratingAI(true); 
        try {
            const aiContent = await generatePackageOverview(yatra_name, city, nights, days, yatra_price);
            
            // This now correctly updates yatra_overview in formData
            handleEditorChange('yatra_overview', aiContent);

            toast.success("AI overview generated successfully!");
        } catch (error) {
            toast.error(error.message || "Failed to generate AI overview. Please try again.");
            console.error("Error calling generatePackageOverview:", error);
        } finally {
            setIsGeneratingAI(false);
        }
    };

    const handleGenerateDescription = async () => {
        const { yatra_name, city, nights, days, offer_price } = formData;
    
        if (!yatra_name || !city || !nights || !days || !offer_price) {
            toast.warn("Please provide Yatra Name, City, Nights, Days, and Price before generating.");
            return;
        }
    
        setIsGeneratingAIDescription(true); 
        try {
            const aiContent = await generatePackageDescription(yatra_name, city, nights, days, offer_price);
            handleEditorChange('description', aiContent);
            toast.success("AI description generated successfully!");
        } catch (error) {
            toast.error(error.message || "Failed to generate AI description. Please try again.");
            console.error("Error calling generatePackageDescription:", error);
        } finally {
            setIsGeneratingAIDescription(false); 
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.yatra_name.trim()) newErrors.yatra_name = 'Yatra name is required.';
        if (!formData.city.trim()) newErrors.city = 'City is required.';
        if (!formData.nights) newErrors.nights = 'Number of nights is required.';
        if (!formData.days) newErrors.days = 'Number of days is required.';
        if (!formData.yatra_price) newErrors.yatra_price = 'Yatra price is required.';
        else if (isNaN(formData.yatra_price) || +formData.yatra_price <= 0) newErrors.yatra_price = 'Yatra price must be a positive number.';
        if (!formData.offer_price) newErrors.offer_price = 'Offer price is required.';
        else if (isNaN(formData.offer_price)) newErrors.offer_price = 'Offer price must be a number.';
        else if (+formData.yatra_price && +formData.offer_price > +formData.yatra_price) newErrors.offer_price = 'Offer price cannot be greater than the Yatra price.';
        if (!formData.description.trim()) newErrors.description = 'Short description is required.';
        if (images.length === 0) newErrors.images = 'Please upload or keep at least one image.';
        
        // UPDATED: Validate against formData.yatra_overview
        if (isEditorEmpty(formData.yatra_overview)) newErrors.yatra_overview = 'Yatra overview is required.';
        
        // Note: hotels are not mandatory
        // How to reach and places to visit are optional.
        
        let hasItineraryError = false;
        const safeItinerary = Array.isArray(itinerary) ? itinerary : normalizeItinerary(itinerary);
        const itineraryErrors = safeItinerary.map((day) => {
            const dayErrors = {};
            if (!String(day.title || '').trim()) { dayErrors.title = 'Title is required.'; hasItineraryError = true; }
            if (!String(day.description || '').trim()) { dayErrors.description = 'Description is required.'; hasItineraryError = true; }
            return dayErrors;
        });
        if (hasItineraryError) newErrors.itinerary = itineraryErrors;
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            toast.error("Please fix the errors before submitting.");
            return;
        }
        
        setIsSubmitting(true);
        const formDataUpload = new FormData();
        
        // Append fields: treat 'hotel_ids' as JSON string and skip 'itinerary' here (we append it explicitly after)
        Object.entries(formData).forEach(([key, val]) => {
            if (key === 'hotel_ids') {
                const safeHotelIds = normalizeHotelIds(val);
                formDataUpload.append(key, JSON.stringify(safeHotelIds));
            } else if (key === 'itinerary') {
                // skip here: we'll append the normalized itinerary explicitly below
            } else {
                formDataUpload.append(key, val ?? '');
            }
        });

        // append normalized itinerary explicitly
        formDataUpload.append('itinerary', JSON.stringify(Array.isArray(itinerary) ? itinerary : normalizeItinerary(itinerary)));
        
        const newImages = images.filter(img => img.file);
        const existingImageUrls = images.filter(img => img.isExisting).map(img => img.url);

        newImages.forEach(img => {
            formDataUpload.append('images', img.file);
        });

        if (isEditMode) {
            formDataUpload.append('existingImages', JSON.stringify(existingImageUrls));
        }

        try {
            const config = { headers: { 'Content-Type': 'multipart/form-data' } };
            const response = isEditMode
                ? await apiClient.put(`/yatras/${id}`, formDataUpload, config)
                : await apiClient.post('/yatras', formDataUpload, config);
            
            toast.success(response.data.message || 'Yatra saved successfully!');
            navigate('/admin/yatras');

        } catch (error) {
            toast.error(error.response?.data?.message || 'An error occurred. Please try again.');
            console.error('Submission failed:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }
    
    // Use safeItinerary in render to avoid runtime errors
    const safeItineraryForRender = Array.isArray(itinerary) ? itinerary : normalizeItinerary(itinerary);

    return (
        <div>
             <div className="row">
                <div className="col-12">
                    <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                        <h4 className="mb-sm-0">{isEditMode ? 'Edit' : 'Add'} Yatra</h4>
                        <div className="page-title-right">
                            <ol className="breadcrumb m-0">
                                <li className="breadcrumb-item"><Link to="/admin/dashboard">Dashboard</Link></li>
                                <li className="breadcrumb-item"><Link to="/admin/yatras">Yatras</Link></li>
                                <li className="breadcrumb-item active">{isEditMode ? 'Edit' : 'Add'}</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} noValidate>
                <div className="row">
                    <div className="col-lg-12">
                        <div className="card">
                            <div className="card-header">
                                <h4 className="card-title mb-0">{isEditMode ? 'Update Yatra Details' : 'Enter Yatra Details'}</h4>
                            </div>
                            <div className="card-body">
                                <div className="accordion" id="yatraAccordion">
                                    <div className="accordion-item">
                                        <h2 className="accordion-header" id="headingOne">
                                            <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#collapseOne">
                                                Yatra Details
                                            </button>
                                        </h2>
                                        <div id="collapseOne" className="accordion-collapse collapse show" data-bs-parent="#yatraAccordion">
                                            <div className="accordion-body">
                                                <div className="row">
                                                      <div className="col-md-9 mb-3">
                                                         <div className={`floating-field position-relative ${formData.yatra_name ? 'has-value' : ''}`} data-bs-toggle="tooltip" data-bs-placement="top" title={isEditMode ? 'Yatra name cannot be changed.' : (errors.yatra_name || '')}>
                                                        <input 
                                                            type="text" 
                                                            className={`form-control ${isEditMode ? 'bg-light' : ''} ${errors.yatra_name ? 'is-invalid' : ''}`} 
                                                            placeholder="e.g., Amazing Shimla Manali Tour" 
                                                            name="yatra_name" 
                                                            value={formData.yatra_name} 
                                                            onChange={handleInputChange} 
                                                            readOnly={isEditMode}
                                                        />
                                                        <label className="floating-label">e.g., Amazing Shimla Manali Tour</label>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3 mb-3">
                                                         <div className={`floating-field position-relative ${formData.city ? 'has-value' : ''}`} data-bs-toggle="tooltip" data-bs-placement="top" title={errors.city || ''}>
                                                            {isLoaded ? (
                                                                <Autocomplete onLoad={(r) => (cityAutoRef.current = r)} onPlaceChanged={onCityPlaceChanged}>
                                                                    <input type="text" className={`form-control ${errors.city ? 'is-invalid' : ''}`} name="city" placeholder='e.g., Shimla' value={formData.city} onChange={handleInputChange} />
                                                                </Autocomplete>
                                                            ) : (
                                                                <input type="text" className={`form-control ${errors.city ? 'is-invalid' : ''}`} name="city" placeholder='e.g., Shimla' value={formData.city} onChange={handleInputChange} />
                                                            )}
                                                            <label className="floating-label">e.g., Shimla</label>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3 mb-3">
                                                        <div data-bs-toggle="tooltip" data-bs-placement="top" title={errors.nights || ''}>
                                                            <DayDropdown label="Night" name="nights" selectedValue={formData.nights} onChange={handleInputChange} className={`${errors.nights ? 'is-invalid' : ''}`} />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3 mb-3">
                                                        <div data-bs-toggle="tooltip" data-bs-placement="top" title={errors.days || ''}>
                                                            <DayDropdown label="Days" name="days" selectedValue={formData.days} onChange={handleInputChange} className={`${errors.days ? 'is-invalid' : ''}`} />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3 mb-3">
                                                         <div className={`floating-field position-relative ${formData.yatra_price ? 'has-value' : ''}`} data-bs-toggle="tooltip" data-bs-placement="top" title={errors.yatra_price || ''}>
                                                        <input type="number" className={`form-control ${errors.yatra_price ? 'is-invalid' : ''}`} name="yatra_price" placeholder='Yatra Price' value={formData.yatra_price} onChange={handleInputChange}  />
                                                        <label className="floating-label">Yatra Price</label>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3 mb-3">
                                                         <div className={`floating-field position-relative ${formData.offer_price ? 'has-value' : ''}`} data-bs-toggle="tooltip" data-bs-placement="top" title={errors.offer_price || ''}>
                                                        <input type="number" className={`form-control ${errors.offer_price ? 'is-invalid' : ''}`} name="offer_price" placeholder='Offer Price' value={formData.offer_price} onChange={handleInputChange}  />
                                                        <label className="floating-label">Offer Price</label>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-12 mb-3">
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <label className="form-label mb-0 fw-medium">Short Description</label>
                                                            <button type="button" className="btn btn-soft-primary btn-sm" onClick={handleGenerateDescription} disabled={isGeneratingAIDescription || isSubmitting}>
                                                                {isGeneratingAIDescription ? (<><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Generating...</>) : (<><i className="ri-robot-2-line me-1"></i>Generate with AI</>)}
                                                            </button>
                                                        </div>
                                                        <div className={`floating-field position-relative ${formData.description ? 'has-value' : ''}`} data-bs-toggle="tooltip" data-bs-placement="top" title={errors.description || ''}>
                                                            <textarea className={`form-control ${errors.description ? 'is-invalid' : ''}`} name="description" placeholder='A brief summary of the yatra...' value={formData.description} onChange={handleInputChange} rows={3}></textarea>
                                                            <label className="floating-label">A brief summary of the yatra...</label>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-12 mb-3">
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <label className="form-label mb-0 fw-medium">Yatra Overview</label>
                                                            <button type="button" className="btn btn-soft-primary btn-sm" onClick={handleGenerateOverview} disabled={isGeneratingAI || isSubmitting}>
                                                                {isGeneratingAI ? (<><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Generating...</>) : (<><i className="ri-robot-2-line me-1"></i>Generate with AI</>)}
                                                            </button>
                                                        </div>
                                                        <div data-bs-toggle="tooltip" data-bs-placement="top" title={errors.yatra_overview || ''} className={errors.yatra_overview ? 'is-invalid-editor' : ''}>
                                                            <CustomEditor
                                                                placeholder="Provide a detailed overview of the yatra..."
                                                                data={formData.yatra_overview}
                                                                onChange={(data) => handleEditorChange('yatra_overview', data)}
                                                                showHtmlMode={false}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                     <div className="accordion-item">
                                        <h2 className="accordion-header" id="headingTwo">
                                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseTwo">
                                                Yatra Images
                                            </button>
                                        </h2>
                                        <div id="collapseTwo" className="accordion-collapse collapse" data-bs-parent="#yatraAccordion">
                                            <div className="accordion-body">
                                                <div className="alert alert-info" role="alert">
                                                    <strong>Note:</strong> You can have a maximum of <strong>6</strong> images.
                                                </div>
                                                <div className="d-flex flex-wrap gap-3" data-bs-toggle="tooltip" data-bs-placement="top" title={errors.images || ''}>
                                                    {images.map((img, idx) => (
                                                        <div key={img.url} className="position-relative" style={{ width: '120px', height: '120px', borderRadius: '8px', overflow: 'hidden' }}>
                                                            <img src={img.url} alt={`Preview ${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                            <button type="button" onClick={() => handleRemoveImage(idx)} className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 p-1" title="Remove Image" style={{ lineHeight: 1 }}>×</button>
                                                        </div>
                                                    ))}
                                                    {images.length < 6 && (
                                                        <label htmlFor="uploadImages" className="d-flex justify-content-center align-items-center" style={{ width: '120px', height: '120px', border: `2px dashed ${errors.images ? '#dc3545' : '#aaa'}`, borderRadius: '8px', cursor: 'pointer', fontSize: '32px', color: '#aaa' }}>
                                                            +
                                                            <input type="file" id="uploadImages" multiple hidden onChange={handleImageChange} accept="image/*" />
                                                        </label>
                                                    )}
                                                </div>
                                                  {errors.images && <div className="d-block mt-2 text-danger">{errors.images}</div>}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="accordion-item">
                                        <h2 className="accordion-header" id="headingThree">
                                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseThree">
                                                Itinerary, Hotels & Other Details
                                            </button>
                                        </h2>
                                        <div id="collapseThree" className="accordion-collapse collapse" data-bs-parent="#yatraAccordion">
                                            <div className="accordion-body">
                                                <h5 className="mb-3">Itinerary ({safeItineraryForRender.length} Days)</h5>
                                                {safeItineraryForRender.map((day, index) => (
                                                    <div key={index} className="border rounded p-3 mb-3 bg-light">
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <label className="fw-bold">Day {index + 1}</label>
                                                            {safeItineraryForRender.length > 1 && (<button type="button" className="btn btn-danger btn-sm" onClick={() => handleRemoveDay(index)}>× Remove Day</button>)}
                                                        </div>
                                                        <div className={`floating-field position-relative ${day.title ? 'has-value' : ''} mb-2`} data-bs-toggle="tooltip" data-bs-placement="top" title={errors.itinerary?.[index]?.title || ''}>
                                                            <input type="text" className={`form-control ${errors.itinerary?.[index]?.title ? 'is-invalid' : ''}`} value={day.title} onChange={(e) => handleDayChange(index, 'title', e.target.value)} placeholder={`Enter Title for Day ${index + 1}`} />
                                                            <label className="floating-label">{`Enter Title for Day ${index + 1}`}</label>
                                                        </div>
                                                        <div className={`floating-field position-relative ${day.description ? 'has-value' : ''}`} data-bs-toggle="tooltip" data-bs-placement="top" title={errors.itinerary?.[index]?.description || ''}>
                                                            <textarea className={`form-control ${errors.itinerary?.[index]?.description ? 'is-invalid' : ''}`} rows={3} value={day.description} onChange={(e) => handleDayChange(index, 'description', e.target.value)} placeholder={`Enter Description for Day ${index + 1}`} ></textarea>
                                                            <label className="floating-label">{`Enter Description for Day ${index + 1}`}</label>
                                                        </div>
                                                    </div>
                                                ))}
                                                <div className="mt-2">
                                                    <button type="button" className="btn btn-info" onClick={handleAddDay}>+ Add Day</button>
                                                </div>
                                                <hr className="my-4" />
                                                <div className="mb-4">
                                                    <h5 className="mb-2">Assign Hotels</h5>
                                                    <div className="row align-items-center">
                                                        <div className="col-md-6 mb-2" data-bs-toggle="tooltip" data-bs-placement="top" title={errors.hotels || ''}>
                                                            <Select options={availableHotelsForSelection} onChange={handleHotelSelect} placeholder= "Search and Select a Hotel" isClearable isSearchable value={null} styles={{ control: (base) => ({...base, borderColor: errors.hotels ? '#f06548' : base.borderColor, boxShadow: errors.hotels ? '0 0 0 0.2rem rgba(240, 101, 72, 0.25)' : 'none' })}} />
                                                        </div>
                                                        <div className="col-md-6 mb-2 d-flex gap-2">
                                                            <a href="/admin/add-hotel" target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary">Create a New Hotel</a>
                                                            <button type="button" className="btn btn-outline-secondary d-flex align-items-center" onClick={fetchHotelsOnly} disabled={isRefreshingHotels}>
                                                                {isRefreshingHotels ? <span className="spinner-border spinner-border-sm me-1"></span> : <span className="me-1">🔄</span>} Refresh
                                                            </button>
                                                        </div>
                                                    </div>
                                                    {assignedHotels.length > 0 && (
                                                        <div className="mt-3 table-responsive">
                                                            <table className="table table-bordered table-striped align-middle">
                                                                <thead className="table-light">
                                                                    <tr><th>#</th><th>Hotel Name</th><th>City</th><th className="text-center">Action</th></tr>
                                                                </thead>
                                                                <tbody>
                                                                    {assignedHotels.map((hotel, index) => (
                                                                        <tr key={hotel.id}><td>{index + 1}</td><td>{hotel.hotel_name}</td><td>{hotel.city}</td><td className="text-center"><button type="button" className="btn btn-sm btn-danger" onClick={() => handleRemoveAssignedHotel(hotel.id)} title="Remove Hotel"><i className="ri-delete-bin-line"></i></button></td></tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </div>
                                                <hr className="my-4" />
                                                <div className="mb-3">
                                                    <label className="form-label">How to Reach</label>
                                                    <div data-bs-toggle="tooltip" data-bs-placement="top" title={errors.how_to_reach || ''} className={errors.how_to_reach ? 'is-invalid-editor' : ''}>
                                                        <CustomEditor data={formData.how_to_reach} onChange={(val) => handleEditorChange('how_to_reach', val)} showHtmlMode={false} />
                                                    </div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="form-label">Places to Visit</label>
                                                    <div className={`floating-field position-relative ${formData.places_to_visit ? 'has-value' : ''}`} data-bs-toggle="tooltip" data-bs-placement="top" title={errors.places_to_visit || ''}>
                                                        <textarea className={`form-control ${errors.places_to_visit ? 'is-invalid' : ''}`} name="places_to_visit" placeholder='e.g., Mall Road, Jakhoo Temple, The Ridge' value={formData.places_to_visit} onChange={handleInputChange} rows={3}></textarea>
                                                        <label className="floating-label">e.g., Mall Road, Jakhoo Temple, The Ridge</label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-4  d-flex justify-content-end gap-2">
                                    <button type="button" className="btn btn-light" onClick={() => navigate('/admin/yatras')} disabled={isSubmitting}>Cancel</button>
                                    <button type="submit" className="btn btn-success w-sm" disabled={isSubmitting}>
                                        {isSubmitting ? 'Submitting...' : (isEditMode ? 'Update Yatra' : 'Submit Yatra')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}

export default FormYatra;
